import * as React from "react";
import { StaticFormulaEditorModal } from "./StaticFormulaEditorModal";
import { useState, useEffect, useMemo } from "react";
import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import FieldFormulaIconLabel from "./FieldFormulaIconLabel";
import { getAllSystemGeneratedFieldNames } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/SystemGeneratedFieldPill";
import { Table, Button, Flex, Search, Pagination, Menu, Text } from "@theorgchartdev/storybook";
import cx from "classnames";
import { FormatFieldModal } from "./FormatFieldModal";
import * as StateLib from "@/Utils/StateLib";
import { DynamicFormulaEditorModal } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/FieldsFormulasConfiguration/DynamicFormulas";
import { ExcelExportFields } from "./ExcelExportFields";
import { getLocalizedPaginationStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { gFormulasLimitKey } from "@coreapp/whitelabel/brand";
import { Globals } from "@coreapp/CoreApp/Globals";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import FieldTypePill from "./FieldTypePill";
import { FIELDS_FORMULAS_TEST_PREFIX } from "./Utils";
import {
  kReservedFieldNames,
  kFormulaOptions,
  enumFeaturesList,
  restrictedInternalFields,
  kHolderCount,
  kReservedFields,
  kPositionCountFields,
  kEmployeeCountFields
} from "@coreapp/constants";
import { getFieldTypeString } from "@coreapp/utils/getFieldType";
import { getFeatureAccess } from "@/Utils/Misc";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { filterInternalFields } from "@/Utils/FilterInternalFields";

export const enumCalcTypes = {
  none: 0,
  predefined: 1,
  dynamic: 2,
  aggregated: 3
};

export const GetFormulaNames = (chartConfig, formulaType, key) => {
  if (!chartConfig) return [];
  const formulas = chartConfig[formulaType];
  let formulaNames = [];
  if (formulas) {
    formulaNames = formulas.map(function (a) {
      return a[key];
    });
  }
  return formulaNames;
};

export const GetChartHeaders = chartConfig => {
  if (!chartConfig) return [];
  var headers = chartConfig["headers"];

  if (headers) {
    var scriptFormulas = chartConfig["scriptFormulas"];

    if (scriptFormulas) {
      var scriptFieldNames = scriptFormulas.map(function (a) {
        return a["formulaName"];
      });
      headers = headers.filter(function (item) {
        return scriptFieldNames.indexOf(item) === -1;
      });
      headers = headers.concat(scriptFieldNames).sort();
    }

    var aggregatedFields = chartConfig["aggregatedFields"];

    if (aggregatedFields) {
      var aggregatedFieldNames = aggregatedFields.map(function (a) {
        return a["name"];
      });
      headers = headers.concat(aggregatedFieldNames).sort();
    }

    var staticFormulas = chartConfig["formulas"];

    if (staticFormulas) {
      if (!Array.isArray(staticFormulas)) {
        var formulas = [];
        for (var aFormulaIdx in staticFormulas) formulas.push(staticFormulas[aFormulaIdx]);
        staticFormulas = formulas;
        chartConfig["formulas"] = staticFormulas;
      }
      var staticFormulaNames = staticFormulas.map(function (a) {
        return a["name"];
      });
      headers = headers.filter(function (item) {
        return staticFormulaNames.indexOf(item) === -1;
      });
      headers = headers.concat(staticFormulaNames).sort();
    }

    headers = headers.concat(
      kReservedFields.filter(function (aField) {
        return !headers.includes(aField);
      })
    );

    headers = headers.filter(function (header) {
      return !restrictedInternalFields.includes(header) || header === kHolderCount;
    });

    headers = filterInternalFields(headers);

    headers.sort();
    return headers;
  }
};

export const FieldsFormulasConfiguration = ({ chartConfig, handleUpdate }) => {
  if (!chartConfig) return;
  let wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();

  useEffect(() => {
    eventTracker.ReportEvent("chart_settings.fields_and_formulas.opened");
  }, []);

  let defaultTableData = { data: [] };
  const [tableData, setTableData] = useState(defaultTableData);
  const [paginatedIndices, setPaginatedIndices] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [editField, setEditField] = useState(null);
  const [editFormula, setEditFormula] = useState(null);
  const [creatingFormula, setCreatingFormula] = useState(false);
  const [creatingDynamicFormula, setCreatingDynamicFormula] = useState(false);
  const [editDynamicFormula, setEditDynamicFormula] = useState(null);

  const { showPricingAndPackaging } = usePermissionCheck();
  const numUnlimitedFormulas = 10000;

  const performSearch = data => {
    return data.filter(item => {
      return item.name.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1;
    });
  };

  const RebuildTableData = () => {
    let data = BuildTableData(chartConfig);

    if (searchInput.length > 0) data = performSearch(data);

    let newTableData = {
      data: data
    };

    setTableData(newTableData);
  };

  useEffect(() => {
    RebuildTableData();
  }, [searchInput, chartConfig, editField]);

  const handleFieldClick = (event, data) => {
    switch (event.detail) {
      case 1:
        break;
      case 2:
        if (data?.staticFormula === true || data?.calc === enumCalcTypes.aggregated)
          openEditFormulaModal(data);
        else openEditFieldFormatModal(data);
        break;
      default:
    }
  };

  const enterCreateFormulaMode = () => {
    setEditFormula(true);
    setCreatingFormula(true);
  };

  const gridOptions = {
    // set background colour on every row, this is probably bad, should be using CSS classes
    // set background colour on even rows again, this looks bad, should be using CSS classes
    getRowStyle: (_data, _rowIndex, isHovered) => {
      if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: handleFieldClick
  };

  const openEditFieldFormatModal = field => {
    setEditField(field);
  };
  const closeFieldFormatModal = (saveData, updateKeys) => {
    setEditField(null);
    if (saveData) {
      handleUpdate(saveData, updateKeys);
    }
  };

  const openEditFormulaModal = data => {
    setCreatingFormula(false); // This assumes that we are editing the formula, not creating one.
    setEditFormula(data.value);
  };

  const openEditDynamicFormulaModal = data => {
    setCreatingDynamicFormula(false); // This assumes that we are editing the formula, not creating one.
  };

  const closeEditFormulaModal = (commitChanges, updateResult, updateKeys) => {
    setEditFormula(null);
    setCreatingFormula(false);
    if (commitChanges) {
      handleUpdate(updateResult, updateKeys);
    }
  };

  const enterCreateDynamicFormulaMode = () => {
    setEditDynamicFormula(true);
    setCreatingDynamicFormula(true);
  };

  const closeEditDynamicFormulaModal = () => {
    setEditDynamicFormula(null);
    setCreatingDynamicFormula(false);
  };
  const handleFieldAction = (action, data) => {
    switch (action) {
      case "editFormat":
        openEditFieldFormatModal(data);
        break;
      case "editFormula":
        openEditFormulaModal(data);
        break;
      case "deleteFormula":
        let theHeader = data.value;
        let formulas = JSON.parse(JSON.stringify(chartConfig.formulas));
        let fieldFormats = JSON.parse(JSON.stringify(chartConfig.fieldFormats));
        let localization = JSON.parse(JSON.stringify(chartConfig.localization));
        let deletedFormulas = JSON.parse(JSON.stringify(chartConfig.deletedFormulas || []));
        let holderFields = chartConfig.holderFields;
        let positionFields = chartConfig.positionFields;

        if (theHeader) {
          let targetFormulaIdx = formulas.map(item => item.name).indexOf(theHeader);
          if (targetFormulaIdx > -1) {
            const targetFormula = formulas[targetFormulaIdx];
            let type = targetFormula?.targetType || "employee";

            formulas.splice(targetFormulaIdx, 1);
            chartConfig.formulas = formulas;
            let updateObj = { formulas: formulas };
            let updateKeys = ["formulas"];
            if (fieldFormats != null) {
              if (fieldFormats[theHeader] != null) {
                delete fieldFormats[theHeader];
                updateObj = { ...updateObj, fieldFormats: fieldFormats };
                updateKeys.push("fieldFormats");
              }
            }
            if (localization?.profile?.[theHeader] != null) {
              delete localization["profile"][theHeader];
              updateObj = { ...updateObj, localization: localization };
              updateKeys.push("localization");
            }

            var headerIndex = chartConfig.headers.indexOf(theHeader);
            if (headerIndex > -1) {
              updateObj = { ...updateObj, headers: [...chartConfig.headers] };
              updateObj.headers.splice(headerIndex, 1);
              updateKeys.push("headers");
            }

            if (holderFields?.includes(theHeader)) {
              updateObj = {
                ...updateObj,
                holderFields: holderFields.filter(field => field !== theHeader)
              };
              updateKeys.push("holderFields");
            }

            if (positionFields?.includes(theHeader)) {
              updateObj = {
                ...updateObj,
                positionFields: positionFields.filter(field => field !== theHeader)
              };
              updateKeys.push("positionFields");
            }

            deletedFormulas.push({
              name: theHeader,
              targetType: type
            });
            updateObj = { ...updateObj, deletedFormulas: deletedFormulas };

            updateKeys.push("deletedFormulas");
            handleUpdate(updateObj, updateKeys);
          }
        }
        break;
      case "editDynamicFormula":
        setEditDynamicFormula(data.id);
        openEditDynamicFormulaModal(data);
        break;
      case "deleteDynamicFormula":
        chartConfig["scriptFormulas"].splice(data.id, 1);
        handleUpdate(chartConfig, "scriptFormulas");
        break;
    }
  };

  const systemGeneratedFields = useMemo(
    () => new Set(getAllSystemGeneratedFieldNames(chartConfig.fields)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(chartConfig.fields)]
  );

  const FieldLabel = ({ rowState }) => {
    return (
      <FieldFormulaIconLabel
        rowState={rowState}
        handleFieldAction={handleFieldAction}
        fieldMapping={chartConfig.fields}
        systemGeneratedFields={systemGeneratedFields}
      />
    );
  };

  const AppliesTo = ({ rowState }) => {
    const appliesTo = rowState?.data?.appliesTo;
    const name = rowState?.data?.name;
    if (!appliesTo) return null;
    return (
      <div className="field-applies-to-pill" data-testid={`field-applies-to-pill-${name}`}>
        <FieldTypePill type={appliesTo} pillProps={{ size: "regular" }} />
      </div>
    );
  };

  const BuildTableColumns = () => {
    if (IsPositionChart(chartConfig)) {
      return [
        {
          field: "name",
          headerName: appstr("Name"),
          type: "cellRenderer",
          columnWidth: "fifty",
          CellRenderer: FieldLabel
        },
        {
          field: "typeName",
          headerName: appstr("Type"),
          type: "defaultCol",
          columnWidth: "twenty-five",
          columnOptions: {
            color: "#8b8b91"
          }
        },
        {
          field: "appliesTo",
          headerName: appstr("Applies to"),
          type: "cellRenderer",
          columnWidth: "twenty-five",
          CellRenderer: AppliesTo
        }
      ];
    } else {
      return [
        {
          field: "name",
          headerName: appstr("Name"),
          type: "cellRenderer",
          columnWidth: "seventy-five",
          CellRenderer: FieldLabel
        },
        {
          field: "typeName",
          headerName: appstr("Type"),
          type: "defaultCol",
          columnOptions: {
            color: "#8b8b91"
          }
        }
      ];
    }
  };
  const BuildDynamicTableColumns = () => {
    return [
      {
        field: "name",
        headerName: appstr("Name"),
        type: "cellRenderer",
        columnWidth: "seventy-five",
        CellRenderer: FieldLabel
      },
      {
        field: "type",
        headerName: appstr("Type"),
        type: "defaultCol",
        columnOptions: {
          color: "#8b8b91"
        }
      }
    ];
  };

  const createMenuOptions = [
    {
      label: appstr("StaticFormula"),
      dataTestId: "fnf-create-static",
      value: 0,
      clickHandler: enterCreateFormulaMode,
      disabled: !getFeatureAccess(enumFeaturesList.staticFormulas)
    },
    {
      label: appstr("Dynamic formula"),
      dataTestId: "fnf-create-dynamic",
      value: 1,
      clickHandler: enterCreateDynamicFormulaMode,
      disabled: !getFeatureAccess(enumFeaturesList.dynamicFormulas)
    }
  ];

  const sectionSearchBar = (
    <Flex width={"300px"}>
      <Search
        clearable
        inputProps={{
          style: {
            fontSize: "14px",
            marginRight: "0",
            marginLeft: "auto"
          },
          spellCheck: false
        }}
        size={"medium"}
        onInput={value => setSearchInput(value)}
        placeholder={appstr("findfield")}
        timeout={200}
        onResetInput={() => setSearchInput("")}
        dataTestId={"fnf-search-input"}
      />
    </Flex>
  );

  let usedFormulas = chartConfig.formulas.length + chartConfig.scriptFormulas.length;
  let remainingFormulas = Math.max(
    0,
    getFeatureAccess(enumFeaturesList.formulasLimit) - usedFormulas
  );

  // Determine message based on account edition
  let formulasMsg = "";
  if (getFeatureAccess(enumFeaturesList.formulasLimit) >= numUnlimitedFormulas) {
    // Enterprise and Pro accounts: show used count
    formulasMsg = appstr("Custom formulas used") + ": " + usedFormulas;
  } else {
    // Other accounts (plus, premium, core): show remaining count
    formulasMsg = appstr("Custom formulas remaining") + ": " + remainingFormulas;
  }

  const createFormulaTrigger = ({ disabled = false } = {}) => (
    <Button
      dataTestId="fnf-btn-create-menu"
      className={cx("oc-special-secondary", "oc-special-secondary--medium")}
      iconProps={{ name: "function" }}
      modifier={"secondary"}
      disabled={disabled}
      tooltipProps={
        disabled
          ? {
              message: appstr(gFormulasLimitKey),
              position: "right-end"
            }
          : null
      }
    >
      {appstr("Create formula")}
    </Button>
  );

  const formulasUpgradeMessage = {
    message: appstr("formulas message"),
    template: appstr("formulas template"),
    formatConfig: { bold1: { type: "bold" } },
    replaceDict: { bold1: appstr("calculate metrics") }
  };

  const formulasCardControls = () => {
    const formulasLimit = getFeatureAccess(enumFeaturesList.formulasLimit);
    const atFormulaLimit = formulasLimit < numUnlimitedFormulas && remainingFormulas <= 0;

    if (!atFormulaLimit) {
      return (
        <Menu optionSize={"medium"} trigger={createFormulaTrigger()} options={createMenuOptions} />
      );
    }
    // Packaging off = upgrade messaging suppressed, but edition limit still applies.
    if (!showPricingAndPackaging) {
      return createFormulaTrigger({ disabled: true });
    }
    return (
      <UpgradePopover
        popoverText={formulasUpgradeMessage}
        featureKey={enumFeaturesList.formulasLimit}
      />
    );
  };

  return (
    <SectionCardGroup>
      <SectionCardControls>
        <Flex width={"100%"} justifyContent={"space-between"}>
          {formulasCardControls()}
        </Flex>
        {editField && (
          <FormatFieldModal
            show={editField != null}
            chartConfig={chartConfig}
            fieldData={editField}
            onClose={closeFieldFormatModal}
            dataTestIdPrefix={FIELDS_FORMULAS_TEST_PREFIX}
          />
        )}
        {editFormula !== null && (
          <StaticFormulaEditorModal
            show={editFormula !== null}
            chartConfig={chartConfig}
            onClose={closeEditFormulaModal}
            name={editFormula}
            creatingFormula={creatingFormula}
            onCreateFormula={closeEditFormulaModal}
            dataTestIdPrefix={FIELDS_FORMULAS_TEST_PREFIX}
          />
        )}
      </SectionCardControls>
      {showPricingAndPackaging && (
        <Text type={"body"} size={"small"}>
          {formulasMsg}
        </Text>
      )}
      <SectionCard title={appstr("Fields and formulas")} titleContent={sectionSearchBar}>
        <Flex direction={"column"} height={"100%"}>
          <div className={"ocn-file-manager-file-container"}>
            <div
              className={cx("ocn-file-manager-file-table", "ocn-list-scrollbar")}
              style={{ borderTop: "none" }}
            >
              <Table
                tableLayout={"fixed"}
                gridOptions={gridOptions}
                columns={BuildTableColumns()}
                data={tableData.data}
                showHeader={true}
                containerPadding={true}
                sortEnabled={true}
                filterEnabled={false}
                rowSize="largeR"
                textSize="mediumT"
                paginatedIndices={paginatedIndices}
              />
              <Pagination
                dataTestIdPrefix={`${FIELDS_FORMULAS_TEST_PREFIX}-pagination-`}
                modifier="full"
                data={tableData.data}
                onDataChange={setPaginatedIndices}
                customStyle={{ padding: "12px 24px" }}
                startPage={1}
                textTags={getLocalizedPaginationStrings()}
                preserveSelectedPage
              />
            </div>
          </div>
        </Flex>
      </SectionCard>
      <ExcelExportFields
        chartConfig={chartConfig}
        handleUpdate={handleUpdate}
        dataTestIdPrefix={FIELDS_FORMULAS_TEST_PREFIX}
      />
      {editDynamicFormula !== null && (
        <DynamicFormulaEditorModal
          show={editDynamicFormula !== null}
          chartConfig={chartConfig}
          onClose={closeEditDynamicFormulaModal}
          name={editDynamicFormula}
          fieldIdx={editDynamicFormula}
          creatingFormula={creatingDynamicFormula}
          onCreateFormula={closeEditDynamicFormulaModal}
          handleUpdate={handleUpdate}
          dataTestIdPrefix={FIELDS_FORMULAS_TEST_PREFIX}
        />
      )}
    </SectionCardGroup>
  );
};

const getFieldAppliesTo = (header, chartConfig) => {
  if (!IsPositionChart(chartConfig)) return null;

  if (kPositionCountFields.includes(header)) {
    return "position";
  }

  if (kEmployeeCountFields.includes(header)) {
    return "employee";
  }

  const staticFormulas = chartConfig["formulas"] || [];
  for (const formula of staticFormulas) {
    if (formula.name === header && formula.targetType) {
      return formula.targetType;
    }
  }

  const scriptFormulas = chartConfig["scriptFormulas"] || [];
  for (const formula of scriptFormulas) {
    if (formula.formulaName === header && formula.targetType) {
      return formula.targetType;
    }
  }

  const positionFields = chartConfig.positionFields || [];

  if (positionFields.includes(header)) {
    return "position";
  }

  return "employee";
};

const BuildTableData = chartConfig => {
  const headers = GetChartHeaders(chartConfig);
  if (!headers) return [];
  return headers.map(header => {
    let fieldData = {
      name: header,
      type: "Text",
      calc: enumCalcTypes.none,
      typeName: appstr("Text"),
      value: header
    };

    var fieldTypes = chartConfig["fieldFormats"];
    var calcType = enumCalcTypes.none;
    var curLangCode = Globals._stringMgr.fCurLangStr;
    if (
      chartConfig["localization"] &&
      chartConfig["localization"]["profile"] &&
      chartConfig["localization"]["profile"][header] &&
      chartConfig["localization"]["profile"][header][curLangCode]
    )
      // replaces direct look up of locale info at gsvrchartmgr to display unsaved changes
      fieldData.label = chartConfig["localization"]["profile"][header][curLangCode];

    var isReservedFieldCheck =
      kReservedFieldNames.indexOf(header.replaceAll(" ", "").toLowerCase()) > -1;

    var fieldType = getFieldTypeString(header, fieldTypes?.[header]?.type);
    if (isReservedFieldCheck) calcType = enumCalcTypes.predefined;

    fieldData.type = fieldType;
    fieldData.typeName = appstr(fieldType);

    const staticFormulas = chartConfig["formulas"] || [];
    const scriptFieldNames = GetFormulaNames(chartConfig, "scriptFormulas", "formulaName");
    const aggregatedFieldNames = GetFormulaNames(chartConfig, "aggregatedFields", "name");

    for (var aMongoIdx in staticFormulas) {
      var theStaticFormula = staticFormulas[aMongoIdx];
      if (header == theStaticFormula["name"]) {
        calcType = enumCalcTypes.predefined;
        fieldData.staticFormula = true;
        break;
      }
    }

    if (scriptFieldNames && scriptFieldNames.indexOf(header) > -1) calcType = enumCalcTypes.dynamic;

    var scriptFormulas = chartConfig["scriptFormulas"];
    for (var aScriptIdx in scriptFormulas) {
      var theFormula = scriptFormulas[aScriptIdx];
      if (theFormula && theFormula["formulaName"]) {
        if (header == theFormula["formulaName"]) {
          var formulaType =
            !theFormula["formulaType"] || kFormulaOptions.indexOf(theFormula["formulaType"]) === -1
              ? "math"
              : theFormula["formulaType"];
          fieldData.type = formulaType;
          fieldData.id = aScriptIdx;
          fieldData.scriptFormulas = true;
        }
      }
    }

    if (aggregatedFieldNames) {
      var aggregatedFieldIdx = aggregatedFieldNames.indexOf(header);
      if (aggregatedFieldIdx > -1) {
        calcType = enumCalcTypes.aggregated;
      }
    }

    fieldData.calc = calcType;
    fieldData.appliesTo = getFieldAppliesTo(header, chartConfig);

    return fieldData;
  });
};
